import { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  FlaskConical, Play, Square, MoreVertical, Trash2, Trophy, Users 
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export default function ABTestList({ onRefresh }) {
  const [abTests, setABTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchABTests = useCallback(async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await api.get(`/ab-tests${params}`);
      setABTests(response.data);
    } catch (error) {
      toast.error("Failed to load A/B tests");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchABTests();
  }, [fetchABTests]);

  const handleStart = async (abTestId) => {
    if (!window.confirm("Start this A/B test? This will send emails to recipients.")) return;
    
    try {
      await api.post(`/ab-tests/${abTestId}/start`);
      toast.success("A/B test started");
      fetchABTests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start test");
    }
  };

  const handleStop = async (abTestId) => {
    if (!window.confirm("Stop this A/B test and calculate winner?")) return;
    
    try {
      const response = await api.post(`/ab-tests/${abTestId}/stop`);
      toast.success(`Test completed! Winner: Variant ${response.data.winner}`);
      fetchABTests();
    } catch (error) {
      toast.error("Failed to stop test");
    }
  };

  const handleDelete = async (abTestId) => {
    if (!window.confirm("Delete this A/B test?")) return;
    
    try {
      await api.delete(`/ab-tests/${abTestId}`);
      toast.success("A/B test deleted");
      fetchABTests();
      onRefresh?.();
    } catch (error) {
      toast.error("Failed to delete test");
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      draft: "badge-draft",
      running: "badge-running",
      completed: "badge-completed",
      cancelled: "badge-cancelled"
    };
    return <Badge className={classes[status] || "badge-draft"}>{status}</Badge>;
  };

  const getTestTypeLabel = (type) => {
    switch (type) {
      case "subject": return "Subject Line";
      case "content": return "Email Content";
      case "send_time": return "Send Time";
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const stats = {
    total: abTests.length,
    draft: abTests.filter(t => t.status === "draft").length,
    running: abTests.filter(t => t.status === "running").length,
    completed: abTests.filter(t => t.status === "completed").length,
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Tests</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-muted-foreground">{stats.draft}</div>
          <div className="stat-label">Draft</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-primary">{stats.running}</div>
          <div className="stat-label">Running</div>
        </div>
        <div className="stat-card">
          <div className="stat-value text-chart-2">{stats.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex justify-end">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="abtest-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tests</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="running">Running</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* A/B Test List */}
      {abTests.length === 0 ? (
        <div className="empty-state">
          <FlaskConical className="empty-state-icon" />
          <h3 className="text-lg font-semibold mb-2">No A/B tests found</h3>
          <p className="text-muted-foreground">
            Create A/B tests to optimize your email campaigns
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {abTests.map((test) => (
            <div 
              key={test.ab_test_id}
              className="bg-card border border-border rounded-lg p-5 card-hover"
              data-testid={`abtest-card-${test.ab_test_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{test.name}</h3>
                  {test.description && (
                    <p className="text-sm text-muted-foreground truncate">
                      {test.description}
                    </p>
                  )}
                </div>
                {getStatusBadge(test.status)}
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Test Type:</span>
                  <span className="font-medium">{getTestTypeLabel(test.test_type)}</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Sample Size:</span>
                  <span className="font-medium flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {test.vessel_ids?.length || 0} recipients
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Traffic Split:</span>
                  <span className="font-medium">
                    {test.traffic_split}% / {100 - test.traffic_split}%
                  </span>
                </div>

                {/* Variant Results */}
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">
                      {test.variant_a_name}
                    </div>
                    <div className="text-sm">
                      <span className="font-mono">{test.variant_a_sent}</span> sent
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {test.variant_a_opens} opens • {test.variant_a_clicks} clicks
                    </div>
                  </div>
                  
                  <div className="p-3 bg-chart-2/10 rounded-lg">
                    <div className="text-xs text-muted-foreground mb-1">
                      {test.variant_b_name}
                    </div>
                    <div className="text-sm">
                      <span className="font-mono">{test.variant_b_sent}</span> sent
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {test.variant_b_opens} opens • {test.variant_b_clicks} clicks
                    </div>
                  </div>
                </div>

                {/* Winner */}
                {test.winner_variant && (
                  <div className="flex items-center gap-2 p-3 bg-chart-3/10 rounded-lg">
                    <Trophy className="h-4 w-4 text-chart-3" />
                    <span className="text-sm font-medium">
                      Winner: Variant {test.winner_variant}
                    </span>
                    {test.confidence_level && (
                      <span className="text-xs text-muted-foreground">
                        ({test.confidence_level.toFixed(0)}% confidence)
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex gap-2">
                  {test.status === "draft" && (
                    <Button
                      size="sm"
                      className="btn-primary"
                      onClick={() => handleStart(test.ab_test_id)}
                      data-testid={`start-abtest-${test.ab_test_id}`}
                    >
                      <Play className="h-3 w-3 mr-1" />
                      Start
                    </Button>
                  )}
                  
                  {test.status === "running" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleStop(test.ab_test_id)}
                    >
                      <Square className="h-3 w-3 mr-1" />
                      Stop & Calculate
                    </Button>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      onClick={() => handleDelete(test.ab_test_id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
